import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion } from "framer-motion";
import { useMemo } from "react";

import { Search, Phone, RotateCw } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import Alert from "../ui/Alert";
import DatePickerCustom from "../ui/DatePicker";
import { Card, CardHeader, CardContent, CardTitle } from "../ui/card";

export interface FormValues {
  dateFrom: string;
  dateTo: string;
  phone: string;
  captcha: string;
}

interface Props {
  onSubmit: (values: FormValues) => void;
  defaultForm: FormValues | undefined;
  errorMessage: string | null;
  isLoading: boolean;
  captchaUrl: string;
  reloadCaptcha: () => void;
  isMobile?: boolean;
}



export const SearchTicketForm = ({
  onSubmit,
  errorMessage,
  isLoading,
  captchaUrl,
  reloadCaptcha,
  defaultForm,
  isMobile,
}: Props) => {
  const { t } = useLanguage();

  const searchSchema = useMemo(
    () =>
      yup.object().shape({
        dateFrom: yup
          .string()
          .required(t("vulongchonngaybatdau"))
          .matches(
            /^\d{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/,
            t("ngaybatdaukhonghople")
          )
          .test("is-valid-date", t("ngaykhonghople"), (value) => {
            if (!value) return false;
            const [year, month, day] = value.split("/").map(Number);
            const date = new Date(year, month - 1, day);
            return (
              date.getFullYear() === year &&
              date.getMonth() === month - 1 &&
              date.getDate() === day
            );
          }),
        dateTo: yup
          .string()
          .required(t("vulongchonngaykethuc"))
          .matches(
            /^\d{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/,
            t("ngaykethuckhonghople")
          )
          .test("is-valid-date", t("ngaykhonghople"), (value) => {
            if (!value) return false;
            const [year, month, day] = value.split("/").map(Number);
            const date = new Date(year, month - 1, day);
            return (
              date.getFullYear() === year &&
              date.getMonth() === month - 1 &&
              date.getDate() === day
            );
          }),
        phone: yup
          .string()
          .required(t("vulongnhapsdt"))
          .matches(
            /^0(3[2-9]|5[6|8|9]|7[06-9]|8[1-5]|8[8-9]|9[0-9])\d{7}$/,
            t("sdtkhonghople")
          ),
        captcha: yup.string().required(t("vulongnhapmaxacnhan")),
      }),
    [t]
  );

  const {
    register,
    control,
    handleSubmit,
    resetField,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(searchSchema as any),
    defaultValues: {
      ...defaultForm,
      captcha: "",
    },
  });

  const onSubmitWrapped = handleSubmit(async (values) => {
    try {
      onSubmit(values);
    } catch (err: any) {
    } finally {
      resetField("captcha", { defaultValue: "" });
      // reloadCaptcha();
    }
  });

  return (
    <motion.div
      key="form"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-4xl mx-auto p-6 space-y-6"
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-medical-primary" />
            {t("tracuulichhen")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="phone"
                className="h-[16px] flex items-center gap-2"
              >
                {t("sodienthoai")} <span className="text-red-500">*</span>
              </Label>
              <input
                {...register("phone")}
                id="phone"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="tel-national"
                placeholder={t("nhapsodienthoai")}
                className={`focus:ring-medical-primary ${
                  isMobile ? "mobile-input-base" : "input-base-small"
                }`}
                onBeforeInput={(e) => {
                  const data = (e as unknown as InputEvent).data ?? "";
                  if (data && /\D/.test(data)) e.preventDefault();
                }}
                onInput={(e) => {
                  const t = e.currentTarget;
                  const cleaned = t.value.replace(/\D+/g, "").slice(0, 12);
                  if (t.value !== cleaned) t.value = cleaned;
                }}
              />
              {errors.phone && (
                <p className="error-text">{errors.phone.message}</p>
              )}
            </div>

            {/* Date From */}
            <div className="space-y-2">
              <Label className="h-[16px] flex items-center gap-2">
                {t("tungay")} <span className="text-red-500">*</span>
              </Label>
              <DatePickerCustom
                name="dateFrom"
                control={control}
                maxDateToday={false}
                isMobile={isMobile}
                allowTyping={false}
              />
              {errors.dateFrom && (
                <p className="error-text">{errors.dateFrom.message}</p>
              )}
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label className="h-[16px] flex items-center gap-2">
                {t("denngay")} <span className="text-red-500">*</span>
              </Label>
              <DatePickerCustom
                name="dateTo"
                control={control}
                maxDateToday={false}
                isMobile={isMobile}
                allowTyping={false}
              />
              {errors.dateTo && (
                <p className="error-text">{errors.dateTo.message}</p>
              )}
            </div>
          </div>
          {/* Captcha */}
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">
              {t("maxacnhan")} <span className="text-red-500">*</span>
            </label>

            <div className="flex flex-wrap items-center gap-3">
              <input
                {...register("captcha")}
                type="text"
                placeholder={t("maxacnhan")}
                className={`${
                  isMobile
                    ? "mobile-input-base w-[110px]"
                    : "input-base-small w-[200px]"
                }`}
              />

              <img
                src={captchaUrl}
                alt="captcha"
                className="border rounded w-[140px]"
              />

              <button onClick={reloadCaptcha}>
                <RotateCw className="w-5 h-5 cursor-pointer" />
              </button>
            </div>
            {errors.captcha && (
              <p className="error-text">{errors.captcha.message}</p>
            )}
          </div>

          {errorMessage && (
            <Alert variant="error" msg={errorMessage} className="mt-2.5" />
          )}
          <Button
            onClick={onSubmitWrapped}
            className={`w-full md:w-auto bg-medical-primary hover:bg-medical-primary/90`}
            disabled={!isValid || isLoading}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t("dangtimkiem")}
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                {t("tracuu")}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};
